import { create } from 'zustand';
import {
  AuthStatus,
  GlobalRole,
  Permission,
  ProjectRole,
  UserStatus,
  type Project,
  type ProjectMember,
  type UserProfile,
} from '@k-ucap/shared';
import type { AuthService, AuthUser } from '@/domain/auth/AuthService.js';
import { createFirebaseAuthService } from '@/domain/auth/FirebaseAuthService.js';
import { createMockAuthService } from '@/domain/auth/MockAuthService.js';
import { PermissionService, type UserContext } from '@/domain/permission/PermissionService.js';
import { getFirebaseStatus } from '@/infrastructure/firebase/firebaseStatus.js';
import { createRepositories } from '@/infrastructure/repositories/RepositoryFactory.js';
import type { Repositories } from '@/infrastructure/repositories/RepositoryInterfaces.js';
import { resetAllStores, resetProjectDependentStores, setCurrentProjectId } from './resetStores.js';

const SELECTED_PROJECT_STORAGE_KEY = 'k-ucap-selected-project-id';

export interface AuthState {
  authService: AuthService;
  repositories: Repositories;
  authUser: AuthUser | null;
  userProfile: UserProfile | null;
  globalRole: GlobalRole;
  projectMemberships: ProjectMember[];
  accessibleProjects: Project[];
  selectedProject: Project | null;
  permissions: Permission[];
  authStatus: AuthStatus;
  isLoading: boolean;
  isSigningIn: boolean;
  error: string | null;

  initializeAuth: () => Promise<(() => void) | undefined>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInAsMock: (role: 'ADMIN' | 'VIEWER') => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  selectProject: (projectId: string | null) => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  canAccessProject: (projectId: string, role?: ProjectRole) => boolean;
  clearError: () => void;
}

const buildUserContext = (userProfile: UserProfile | null, memberships: ProjectMember[]): UserContext | null => {
  if (!userProfile) return null;
  const projectRoles = new Map<string, ProjectRole>();
  memberships.forEach((m) => projectRoles.set(m.projectId, m.projectRole));
  return {
    uid: userProfile.uid,
    globalRole: userProfile.globalRole,
    status: userProfile.status,
    projectRoles,
  };
};

const createServices = () => {
  const status = getFirebaseStatus();
  const useMock = import.meta.env?.VITE_FORCE_MOCK_MODE === 'true' || status.mode !== 'FIREBASE';
  const authService = useMock ? createMockAuthService() : createFirebaseAuthService();
  const repositories = createRepositories(useMock ? 'MOCK' : 'FIREBASE');
  return { authService, repositories };
};

const loadStoredProjectId = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY);
  } catch {
    return null;
  }
};

const storeProjectId = (projectId: string | null) => {
  try {
    if (projectId) {
      localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, projectId);
    } else {
      localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};

export const useAuthStore = create<AuthState>((set, get) => {
  const { authService, repositories } = createServices();

  const computePermissions = (profile: UserProfile | null, memberships: ProjectMember[]) => {
    const ctx = buildUserContext(profile, memberships);
    return new PermissionService(ctx).resolvePermissions();
  };

  let authInitPromise: Promise<(() => void) | undefined> | null = null;

  const determineAccessibleProjects = async (
    profile: UserProfile,
    memberships: ProjectMember[]
  ): Promise<Project[]> => {
    try {
      if (profile.globalRole === GlobalRole.ADMIN) {
        const all = await repositories.projectRepository.findByEnvironment();
        return all;
      }
      if (memberships.length === 0 && profile.globalRole === GlobalRole.GUEST) {
        const demo = await repositories.projectRepository.findById('k-ucap-demo-001');
        return demo ? [demo] : [];
      }
      const membershipProjectIds = new Set(memberships.map((m) => m.projectId));
      const projects = await repositories.projectRepository.findByEnvironment();
      const accessible = projects.filter((p) => membershipProjectIds.has(p.id) || p.isDemo);
      return accessible;
    } catch (err) {
      console.error('Accessible projects load failed:', err);
      return [];
    }
  };

  const selectBestProject = (
    profile: UserProfile,
    memberships: ProjectMember[],
    accessibleProjects: Project[]
  ): Project | null => {
    const candidates = [
      loadStoredProjectId(),
      profile.defaultProjectId,
      profile.globalRole === GlobalRole.ADMIN ? 'k-ucap-dev-001' : null,
      memberships[0]?.projectId,
    ].filter((id): id is string => Boolean(id));

    for (const id of candidates) {
      const found = accessibleProjects.find((p) => p.id === id);
      if (found) return found;
    }

    const demoProject = accessibleProjects.find((p) => p.isDemo);
    if (demoProject) return demoProject;

    return accessibleProjects[0] ?? null;
  };

  const loadProfile = async (authUser: AuthUser) => {
    try {
      set({ authStatus: AuthStatus.PROFILE_LOADING, authUser, isSigningIn: false });
      const profile = await repositories.userRepository.ensureDefaultProfile(authUser, authUser.authProvider);
      if (profile.status === UserStatus.SUSPENDED || profile.status === UserStatus.DISABLED) {
        set({
          authStatus: AuthStatus.ACCESS_DENIED,
          error: '계정이 사용 중지되었습니다.',
          isLoading: false,
          isSigningIn: false,
        });
        return;
      }

      let memberships: ProjectMember[] = [];
      try {
        if (profile.membershipIds && profile.membershipIds.length > 0) {
          const results = await Promise.all(
            profile.membershipIds.map((id) => repositories.projectMemberRepository.findById(id))
          );
          memberships = results.filter((m): m is ProjectMember => m !== undefined);
        }
        // Non-admin users cannot list projectMembers; rely on membershipIds or demo mode
      } catch (membershipErr) {
        console.error('Membership load failed:', membershipErr);
        memberships = [];
      }

      const accessibleProjects = await determineAccessibleProjects(profile, memberships);
      const selectedProject = selectBestProject(profile, memberships, accessibleProjects);
      if (selectedProject) {
        storeProjectId(selectedProject.id);
        if (profile.defaultProjectId !== selectedProject.id) {
          try {
            await repositories.userRepository.updateDefaultProject(profile.uid, selectedProject.id);
          } catch (e) {
            console.error('updateDefaultProject failed:', e);
          }
        }
      }

      const permissions = computePermissions(profile, memberships);
      set({
        userProfile: profile,
        globalRole: profile.globalRole,
        projectMemberships: memberships,
        accessibleProjects,
        selectedProject,
        permissions,
        authStatus: AuthStatus.READY,
        isLoading: false,
        isSigningIn: false,
        error: null,
      });
    } catch (err) {
      console.error('loadProfile error:', err);
      set({ error: (err as Error).message, authStatus: AuthStatus.ERROR, isLoading: false, isSigningIn: false });
      throw err;
    }
  };

  const clearSession = () => {
    storeProjectId(null);
    resetAllStores();
    set({
      authUser: null,
      userProfile: null,
      globalRole: GlobalRole.GUEST,
      projectMemberships: [],
      accessibleProjects: [],
      selectedProject: null,
      permissions: [],
      authStatus: AuthStatus.UNAUTHENTICATED,
      isLoading: false,
      isSigningIn: false,
      error: null,
    });
  };

  return {
    authService,
    repositories,
    authUser: null,
    userProfile: null,
    globalRole: GlobalRole.GUEST,
    projectMemberships: [],
    accessibleProjects: [],
    selectedProject: null,
    permissions: [],
    authStatus: AuthStatus.INITIALIZING,
    isLoading: true,
    isSigningIn: false,
    error: null,

    initializeAuth: async () => {
      if (authInitPromise) return authInitPromise;
      authInitPromise = (async () => {
        try {
          await authService.initialize();
          const unsubscribe = authService.subscribeAuthState(async (user) => {
            if (user) {
              try {
                await loadProfile(user);
              } catch (err) {
                console.error('Auth listener load profile error:', err);
                set({
                  authStatus: AuthStatus.ERROR,
                  error: (err as Error).message,
                  isLoading: false,
                  isSigningIn: false,
                });
              }
            } else {
              clearSession();
            }
          });
          return unsubscribe;
        } catch (err) {
          set({ authStatus: AuthStatus.ERROR, error: (err as Error).message, isLoading: false, isSigningIn: false });
          return undefined;
        }
      })();
      return authInitPromise;
    },

    signInWithEmail: async (email, password) => {
      set({ isSigningIn: true, error: null });
      try {
        const user = await authService.signInWithEmail(email, password);
        await loadProfile(user);
      } catch (err) {
        set({ error: (err as Error).message, isSigningIn: false, authStatus: AuthStatus.UNAUTHENTICATED });
      }
    },

    signInWithGoogle: async () => {
      set({ isSigningIn: true, error: null });
      try {
        const user = await authService.signInWithGoogle();
        await loadProfile(user);
      } catch (err) {
        set({ error: (err as Error).message, isSigningIn: false, authStatus: AuthStatus.UNAUTHENTICATED });
      }
    },

    signInAsMock: async (role) => {
      set({ isSigningIn: true, error: null });
      try {
        const user = await authService.signInAsMock(role);
        const mockRole = role === 'ADMIN' ? GlobalRole.ADMIN : GlobalRole.VIEWER;
        const profile = await repositories.userRepository.ensureDefaultProfile(user, 'mock');
        const updated: UserProfile = { ...profile, globalRole: mockRole, status: UserStatus.ACTIVE };
        await repositories.userRepository.save(updated);
        await loadProfile(user);
      } catch (err) {
        set({ error: (err as Error).message, isSigningIn: false, authStatus: AuthStatus.UNAUTHENTICATED });
      }
    },

    signOut: async () => {
      set({ isSigningIn: true });
      try {
        await authService.signOut();
        clearSession();
      } catch (err) {
        set({ error: (err as Error).message, isSigningIn: false });
      }
    },

    sendPasswordReset: async (email) => {
      set({ isSigningIn: true, error: null });
      try {
        await authService.sendPasswordResetEmail(email);
        set({ isSigningIn: false });
      } catch (err) {
        set({ error: (err as Error).message, isSigningIn: false });
      }
    },

    selectProject: async (projectId) => {
      if (!projectId) {
        storeProjectId(null);
        resetProjectDependentStores();
        setCurrentProjectId(null);
        set({ selectedProject: null });
        return;
      }
      resetProjectDependentStores();
      set({ selectedProject: null });
      const project = await repositories.projectRepository.findById(projectId);
      if (project) {
        const profile = get().userProfile;
        if (profile) {
          try {
            await repositories.userRepository.updateDefaultProject(profile.uid, projectId);
          } catch (e) {
            console.error('updateDefaultProject failed:', e);
          }
        }
        storeProjectId(projectId);
        setCurrentProjectId(projectId);
        set({ selectedProject: project });
      }
    },

    hasPermission: (permission) => {
      const { userProfile, projectMemberships, selectedProject } = get();
      const ctx = buildUserContext(userProfile, projectMemberships);
      return new PermissionService(ctx, { isDemoProject: selectedProject?.isDemo ?? false }).hasPermission(permission);
    },

    canAccessProject: (projectId, role) => {
      const { userProfile, projectMemberships, selectedProject } = get();
      const ctx = buildUserContext(userProfile, projectMemberships);
      return new PermissionService(ctx, { isDemoProject: selectedProject?.isDemo ?? false }).canAccessProject(
        projectId,
        role
      );
    },

    clearError: () => set({ error: null }),
  };
});
